import { observable, computed } from 'mobx';
import Config from '../../domain/entities/Config';
import ConfigRepository from '../../repositories/ConfigRepository';
import TransactionRepository from '../../repositories/TransactionRepository';
import Transaction from '../../domain/entities/Transaction/Transaction';
import CreditCard from '../../domain/entities/CreditCard/CreditCard';
import Payment from '../../domain/entities/Payment/Payment';
import { PAYMENT_MODES } from '../../constants/PaymentProviders';

export interface ICreditCardsViewControllerConfig {
  configRepository: ConfigRepository;
  transactionRepository: TransactionRepository;
}

export interface ICreditCardsViewControllerData {
  creditCards: CreditCard[];
  selectedOptionId: string;
  showCCForm: boolean;
}

export default class CreditCardsViewController {
  configRepository: ConfigRepository;

  transactionRepository: TransactionRepository;

  @observable config: Config;

  @observable transaction: Transaction;

  @observable creditCards: CreditCard[];

  @observable payment: Payment;

  @observable selectedCardId: string;

  constructor(config: ICreditCardsViewControllerConfig) {
    this.transactionRepository = config.transactionRepository;
    this.configRepository = config.configRepository;
  }

  loaded = async (): Promise<void> => {
    this.config = await this.configRepository.getConfig();
    this.transaction = await this.transactionRepository.getTransaction();
    this.creditCards = this.transaction.creditCards || [];
    this.payment = this.transaction.payment;
    this.setDefaultOrFirstAsSelectedCard();
  };

  setDefaultOrFirstAsSelectedCard(): void {
    // enhanced cards will set default to the top, so just set the first one
    if (this.enhancedCreditCards.length > 0) {
      this.selectedCardId = this.enhancedCreditCards[0].userPaymentMethodId;
    } else {
      this.selectedCardId = PAYMENT_MODES.NEW_CREDIT_CARD_PAYMENT_ID;
    }
  }

  onSelectedPaymentCardId = (optionId: string): void => {
    this.selectedCardId = optionId;
  };

  @computed
  get data(): ICreditCardsViewControllerData {
    return {
      creditCards: this.enhancedCreditCards,
      selectedOptionId: this.selectedCardId,
      showCCForm: this.shouldDisplayCCForm,
    };
  }

  @computed
  get shouldDisplayCCForm(): boolean {
    // display CC form if option selected or has no credit cards saved
    return this.selectedCardId === PAYMENT_MODES.NEW_CREDIT_CARD_PAYMENT_ID
      || !this.enhancedCreditCards.length;
  }

  @computed
  get enhancedCreditCards(): CreditCard[] {
    const payment = this.transaction?.payment;
    const acceptedCreditCards = payment
      ? payment.getAcceptedCreditCards(payment.selectedPayment, payment.selectedPaymentMode) : [];

    const enhancedCreditCardItems = (this.creditCards || []).map(item => {
      const acceptedCC = (acceptedCreditCards || [])
        .find(acc => acc.type.toLowerCase() === item.type.toLowerCase());

      return acceptedCC ? CreditCard.fromJSON({
        ...item,
        alternateText: acceptedCC.alt,
        imageSrc: acceptedCC.image,
      }) : item;
    });

    // order by isDefault flag
    return enhancedCreditCardItems.sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
  }
}
